import { t } from "i18next";
import toast from "react-hot-toast";
import BasicDialog from "../../components/base/BasicDialog";
import { SnackbarError } from "../../components/base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";
import { useToasts } from "../../hooks/useToasts";
import { useGetAllClauses } from "../../services/clausesService";
import {
    useAddLeadMutation,
    useLeadFailureReasons,
    useLeadsSourceChannels,
    useLeadsStatuses,
} from "../../services/leadsService";
import { useGetSellers } from "../../services/usersApi";
import { LeadForm } from "./LeadForm";

import ConfirmationDialog from "../../components/dialog/ConfirmationDialog/confirmationDialog";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import { externalLeadSources } from "../../helpers/constants";
import { useInvestments } from "../../services/productsService";

export function LeadAddDialog(props) {
    const { defaultData, disablePersonFields, personId } = props;

    const { mutate: addLead, isPending } = useAddLeadMutation({
        onError: (error) => {
            toast.custom((curToast) => <SnackbarError message={t("lead.addLeadForm.unknownErrorOccured")} />, {
                position: "bottom-right",
            });
        },
        onSuccess: () => {
            toast.custom(
                (curToast) => <SnackbarSuccess message={t("lead.addLeadForm.addedSuccessfully")} />,
                {
                    position: "bottom-right",
                },
            );
            props.onClose();
            props.onSuccessEdit?.();
        },
    });

    const { data: leadsSourceChannels } = useLeadsSourceChannels({
        select: (data) =>
            data.results.map((channel) => ({
                value: channel.channel_name_eng,
                label: t(`lead.sources.${channel.channel_name_eng}`),
            })),
    });
    const { data: leadsStatuses } = useLeadsStatuses({
        select: (data) =>
            data.results.map((status) => ({
                value: status.status_name_eng,
                label: t(`lead.status.${status.status_name_eng}`),
            })),
    });
    const { data: sellers } = useGetSellers({
        select: (data) =>
            data.map((seller) => ({
                value: seller.id,
                label: seller.full_name,
            })),
    });

    const { data: failureReasons } = useLeadFailureReasons({
        select: (data) =>
            data.results.map((reason) => ({
                value: reason.reason_name_eng,
                label: t(`lead.reasons.${reason.reason_name_eng}`),
            })),
    });

    const { data: investments } = useInvestments({
        select: (data) =>
            data.results.map((investment) => ({
                value: investment.id,
                label: investment.investment_name_sales,
            })),
    });
    const { onFormErrors } = useToasts();

    const { openDialog: openSendEmailDialog, closeDialog: closeSendEmailDialog } =
        useDialogBetter();

    const handleSubmit = (form) => {
        const tempForm = {
            leadSource: form.leadSource,
            leadStatus: form.leadStatus,
            statusNote: form.statusNote,
            leadStatusReason: form.statusReason ?? "",
            leadInvestment: form.leadInvestment,
            personEmail: form.personEmail,
            personName: form.personName,
            personSurname: form.personSurname,
            personPhone: form.personPhone,
            sellerComment: form.sellerComment,
            leadCaretakerId: form.leadCaretakerId,
            personId: personId,
            productIds: form.productIds,
        };

        if (!externalLeadSources.includes(tempForm.leadSource.toLowerCase())) {
            addLead(tempForm, {});
            return;
        }

        openSendEmailDialog({
            Component: ConfirmationDialog,
            componentProps: {
                title: t("lead.addLeadForm.emailTitle"),
                content: t("lead.addLeadForm.emailContent"),
                showCloseButton: true,
                onYes: () => {
                    if (!tempForm.personEmail) {
                    toast.custom((curToast) => <SnackbarError message={t("lead.addLeadForm.enterEmailToSendAutoresponder")} />, {position: "bottom-right"})
                    } else {
                        tempForm.send_email = true;
                        addLead(tempForm, {});
                    }
                    closeSendEmailDialog();
                },
                onNo: () => {
                    tempForm.send_email = false;
                    addLead(tempForm, {});
                    closeSendEmailDialog();
                },
                onCancel: () => {
                    closeSendEmailDialog();
                },
            },
        });
    };

    if (!leadsSourceChannels || !leadsStatuses || !sellers || !failureReasons || !investments) {
        return null;
    }

    return (
        // @ts-ignore
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("lead.addLeadForm.title")}
            maxWidth={"sm"}
            showDialogActions
        >
            <LeadForm
                submitBtnText={t("add")}
                failureReasons={failureReasons}
                leadsSourceChannels={leadsSourceChannels}
                leadsStatuses={leadsStatuses}
                defaultValues={{
                    personName: defaultData?.firstName || "",
                    personSurname: defaultData?.lastName || "",
                    personEmail: defaultData?.email || null,
                    personPhone: defaultData?.phone || null,
                    leadSource: "",
                    statusNote: "",
                    leadStatus: "",
                    statusReason: "",
                    leadInvestment: "",
                    productIds: [],
                }}
                disablePersonFields={disablePersonFields}
                sellers={sellers}
                investments={investments}
                onSubmit={(form) => handleSubmit(form)}
                onError={onFormErrors}
                isLoading={isPending}
            />
        </BasicDialog>
    );
}
